/**
 * ContactModalValidator.js 
 * -------------------------------
 * Location: src/scripts/contact/core/ContactModalValidator.js
 * 
 * Dedicated validation system for contact modal fields.
 * Fixed with fallback validation messages to prevent undefined errors.
 */
import { FormValidator } from '../utils/FormValidator.js';
import { CONSTANTS, VALIDATION_MESSAGES } from './ContactModalConstants.js';

// Fallback validation messages in case VALIDATION_MESSAGES is undefined
const FALLBACK_VALIDATION_MESSAGES = {
  FIRSTNAME: {
    REQUIRED: 'First name is required',
    MIN_LENGTH: (min) => `First name must be at least ${min} characters`,
    MAX_LENGTH: (max) => `First name must be less than ${max} characters`,
    PATTERN: 'First name can only contain letters, spaces, hyphens, and apostrophes'
  },
  LASTNAME: {
    REQUIRED: 'Last name is required',
    MIN_LENGTH: (min) => `Last name must be at least ${min} characters`,
    MAX_LENGTH: (max) => `Last name must be less than ${max} characters`,
    PATTERN: 'Last name can only contain letters, spaces, hyphens, and apostrophes'
  },
  EMAIL: {
    REQUIRED: 'Email is required',
    INVALID: 'Please enter a valid email address',
    MAX_LENGTH: (max) => `Email address must be less than ${max} characters`
  },
  PHONE: {
    REQUIRED: 'Phone number is required',
    INVALID: 'Please enter a valid phone number'
  },
  MESSAGE: {
    REQUIRED: 'Message is required',
    MIN_LENGTH: (min) => `Message must be at least ${min} characters`,
    MAX_LENGTH: (max) => `Message must be less than ${max} characters`
  },
  ROLE: {
    REQUIRED: 'Please choose your role',
    MAX_LENGTH: (max) => `Role must be less than ${max} characters`
  },
  PROGRAM: {
    REQUIRED: 'Please choose a program',
    MAX_LENGTH: (max) => `Program must be less than ${max} characters`
  }
};

/**
 * Redesign form fields (spec §5.13.4) checked only when the form contains them.
 * Option lists are enforced server-side; here we check required and length.
 */
const OPTIONAL_FORM_FIELDS = ['reason', 'who', 'role', 'method', 'program'];
const OPTIONAL_FIELD_MAX_LENGTH = { program: 200 };

// Safe getter for validation messages with fallback
function getValidationMessages(fieldName) {
  const upperFieldName = fieldName.toUpperCase();
  
  // Try to use imported VALIDATION_MESSAGES first, then fallback
  if (VALIDATION_MESSAGES && VALIDATION_MESSAGES[upperFieldName]) {
    return VALIDATION_MESSAGES[upperFieldName];
  }
  
  // Use fallback if import failed or message doesn't exist
  return FALLBACK_VALIDATION_MESSAGES[upperFieldName] || {
    REQUIRED: `${fieldName} is required`,
    MIN_LENGTH: (min) => `${fieldName} must be at least ${min} characters`,
    MAX_LENGTH: (max) => `${fieldName} must be less than ${max} characters`,
    PATTERN: `Please enter a valid ${fieldName}`,
    INVALID: `Please enter a valid ${fieldName}`
  };
}

/**
 * Validation rules manager for contact modal
 */
class ValidationRulesManager {
  constructor() {
    this.rules = this.getDefaultRules();
  }

  getDefaultRules() {
    // Safe access to CONSTANTS with fallbacks
    const defaults = CONSTANTS?.VALIDATION_DEFAULTS || {
      MESSAGE: { MIN_LENGTH: 0, MAX_LENGTH: 2000, REQUIRED: false },
      FIRST_NAME: { MIN_LENGTH: 2, MAX_LENGTH: 50, REQUIRED: true },
      LAST_NAME: { MIN_LENGTH: 0, MAX_LENGTH: 50, REQUIRED: false },
      EMAIL: { MAX_LENGTH: 254, REQUIRED: true },
      PHONE: { REQUIRED: false }
    };

    return {
      message: { ...defaults.MESSAGE },
      firstName: { ...defaults.FIRST_NAME },
      lastName: { ...defaults.LAST_NAME },
      email: { ...defaults.EMAIL },
      phone: { ...defaults.PHONE },
      ...this.getOptionalFieldRules()
    };
  }

  getOptionalFieldRules() {
    return Object.fromEntries(OPTIONAL_FORM_FIELDS.map((field) => [
      field,
      { required: false, maxLength: OPTIONAL_FIELD_MAX_LENGTH[field] || 100 }
    ]));
  }

  /**
   * Load validation rules from form dataset
   */
  loadFromDataset(formElement) {
    if (!formElement) return;

    const dataset = formElement.dataset;
    const defaults = this.getDefaultRules();
    
    this.rules = {
      message: {
        minLength: parseInt(dataset.validationMessageMinLength) || defaults.message.minLength || 0,
        maxLength: parseInt(dataset.validationMessageMaxLength) || defaults.message.maxLength || 2000,
        required: dataset.validationMessageRequired === 'true'
      },
      firstName: {
        minLength: parseInt(dataset.validationFirstnameMinLength) || defaults.firstName.minLength || 2,
        maxLength: parseInt(dataset.validationFirstnameMaxLength) || defaults.firstName.maxLength || 50,
        required: true
      },
      lastName: {
        minLength: defaults.lastName.minLength || 0,
        maxLength: parseInt(dataset.validationLastnameMaxLength) || defaults.lastName.maxLength || 50,
        required: false
      },
      email: {
        maxLength: parseInt(dataset.validationEmailMaxLength) || defaults.email.maxLength || 254,
        required: true
      },
      phone: {
        required: dataset.validationPhoneRequired === 'true'
      },
      ...this.getOptionalFieldRules()
    };
  }

  /**
   * Update validation rules from modal configuration
   */
  updateFromConfig(config) {
    if (config.messageRequired !== undefined) {
      this.rules.message.required = config.messageRequired;
    }
    if (config.messageMinLength !== undefined) {
      this.rules.message.minLength = config.messageMinLength;
    }
    if (config.messageMaxLength !== undefined) {
      this.rules.message.maxLength = config.messageMaxLength;
    }
    if (config.phoneRequired !== undefined) {
      this.rules.phone.required = config.phoneRequired;
    }
    // Disclosure mode requires a role; the closed-event notify mode a program.
    this.rules.role.required = config.roleRequired === true;
    this.rules.program.required = config.programRequired === true;
  }

  /**
   * Get validation rules for a specific field
   */
  getFieldRules(fieldName) {
    return this.rules[fieldName] || {};
  }

  /**
   * Update specific field rules
   */
  updateFieldRules(fieldName, newRules) {
    this.rules[fieldName] = { ...this.rules[fieldName], ...newRules };
  }

  /**
   * Get all current rules
   */
  getAllRules() {
    return { ...this.rules };
  }
}

/**
 * Contact form field validator
 */
class ContactFieldValidator {
  constructor(rulesManager) {
    this.rulesManager = rulesManager;
  }

  /**
   * Validate a single field based on its name and value
   */
  validateField(fieldName, value) {
    const trimmedValue = value?.trim() || '';

    switch (fieldName) {
      case 'firstName':
        return this.validateNameField(fieldName, trimmedValue, true);
      case 'lastName':
        return this.validateNameField(fieldName, trimmedValue, false);
      case 'email':
        return this.validateEmailField(trimmedValue);
      case 'phone':
        return this.validatePhoneField(trimmedValue);
      case 'message':
        return this.validateMessageField(trimmedValue);
      case 'reason':
      case 'who':
      case 'role':
      case 'method':
      case 'program':
        return this.validateOptionalField(fieldName, trimmedValue);
      default:
        return { valid: true, error: null };
    }
  }

  /**
   * Validate a redesign select/text field: required (when the mode says so) and length.
   */
  validateOptionalField(fieldName, value) {
    const rules = this.rulesManager.getFieldRules(fieldName);
    const messages = getValidationMessages(fieldName);

    if (rules.required && !value) {
      return { valid: false, error: { field: fieldName, message: messages.REQUIRED } };
    }
    if (value && rules.maxLength && value.length > rules.maxLength) {
      return { valid: false, error: { field: fieldName, message: messages.MAX_LENGTH(rules.maxLength) } };
    }
    return { valid: true, error: null };
  }

  /**
   * Validate name fields (firstName, lastName) - FIXED with safe message access
   */
  validateNameField(fieldName, value, forceRequired = false) {
    const rules = this.rulesManager.getFieldRules(fieldName);
    const required = forceRequired || rules.required;
    const minLength = rules.minLength || 0;
    const maxLength = rules.maxLength || 50;
    
    // Use safe message getter with fallback
    const messages = getValidationMessages(fieldName);

    if (required && !value) {
      return {
        valid: false,
        error: { field: fieldName, message: messages.REQUIRED }
      };
    }

    if (value && value.length < minLength) {
      return {
        valid: false,
        error: { 
          field: fieldName, 
          message: typeof messages.MIN_LENGTH === 'function' ? 
                  messages.MIN_LENGTH(minLength) : 
                  `${fieldName === 'firstName' ? 'First name' : 'Last name'} must be at least ${minLength} characters`
        }
      };
    }

    if (value && value.length > maxLength) {
      return {
        valid: false,
        error: { 
          field: fieldName, 
          message: typeof messages.MAX_LENGTH === 'function' ? 
                  messages.MAX_LENGTH(maxLength) : 
                  `${fieldName === 'firstName' ? 'First name' : 'Last name'} must be less than ${maxLength} characters`
        }
      };
    }

    // Safe regex check
    const namePattern = CONSTANTS?.REGEX?.NAME_PATTERN || /^[a-zA-Z\s\-']+$/;
    if (value && !namePattern.test(value)) {
      return {
        valid: false,
        error: { field: fieldName, message: messages.PATTERN }
      };
    }

    return { valid: true, error: null };
  }

  /**
   * Validate email field - FIXED with safe message access
   */
  validateEmailField(value) {
    const rules = this.rulesManager.getFieldRules('email');
    const messages = getValidationMessages('email');

    if (!value) {
      return {
        valid: false,
        error: { field: 'email', message: messages.REQUIRED }
      };
    }

    if (!FormValidator.isValidEmail(value)) {
      return {
        valid: false,
        error: { field: 'email', message: messages.INVALID }
      };
    }

    const maxLength = rules.maxLength || 254;
    if (value.length > maxLength) {
      return {
        valid: false,
        error: { 
          field: 'email', 
          message: typeof messages.MAX_LENGTH === 'function' ? 
                  messages.MAX_LENGTH(maxLength) : 
                  `Email address must be less than ${maxLength} characters`
        }
      };
    }

    return { valid: true, error: null };
  }

  /**
   * Validate phone field - FIXED with safe message access
   */
  validatePhoneField(value) {
    const rules = this.rulesManager.getFieldRules('phone');
    const messages = getValidationMessages('phone');
    const required = rules.required || false;

    if (required && !value) {
      return {
        valid: false,
        error: { field: 'phone', message: messages.REQUIRED }
      };
    }

    if (value && !FormValidator.isValidPhone(value)) {
      return {
        valid: false,
        error: { field: 'phone', message: messages.INVALID }
      };
    }

    return { valid: true, error: null };
  }

  /**
   * Validate message field - FIXED with safe message access
   */
  validateMessageField(value) {
    const rules = this.rulesManager.getFieldRules('message');
    const messages = getValidationMessages('message');
    const required = rules.required || false;
    const minLength = rules.minLength || 0;
    const maxLength = rules.maxLength || 2000;

    if (required && !value) {
      return {
        valid: false,
        error: { field: 'message', message: messages.REQUIRED }
      };
    }

    if (value && minLength > 0 && value.length < minLength) {
      return {
        valid: false,
        error: { 
          field: 'message', 
          message: typeof messages.MIN_LENGTH === 'function' ? 
                  messages.MIN_LENGTH(minLength) : 
                  `Message must be at least ${minLength} characters`
        }
      };
    }

    if (value && value.length > maxLength) {
      return {
        valid: false,
        error: { 
          field: 'message', 
          message: typeof messages.MAX_LENGTH === 'function' ? 
                  messages.MAX_LENGTH(maxLength) : 
                  `Message must be less than ${maxLength} characters`
        }
      };
    }

    return { valid: true, error: null };
  }
}

/**
 * Complete contact form validator
 */
class ContactFormValidator {
  constructor() {
    this.rulesManager = new ValidationRulesManager();
    this.fieldValidator = new ContactFieldValidator(this.rulesManager);
  }

  /**
   * Initialize validator with form element
   */
  initialize(formElement) {
    this.rulesManager.loadFromDataset(formElement);
  }

  /**
   * Update validation rules from modal configuration
   */
  updateFromConfig(config) {
    this.rulesManager.updateFromConfig(config);
  }

  /**
   * Validate entire form
   */
  validateForm(formElement) {
    const errors = [];
    let valid = true;

    const formData = new FormData(formElement);
    const data = {
      firstName: formData.get('firstName')?.trim() || '',
      lastName: formData.get('lastName')?.trim() || '',
      email: formData.get('email')?.trim() || '',
      phone: formData.get('phone')?.trim() || '',
      message: formData.get('message')?.trim() || ''
    };
    // Redesign fields only when this form renders them (and they're enabled).
    // A radio group with nothing chosen is absent from FormData, so also
    // check for an enabled control; otherwise a required role is never flagged.
    OPTIONAL_FORM_FIELDS.forEach((field) => {
      const rendered = formData.has(field)
        || !!formElement.querySelector?.(`[name="${field}"]:not(:disabled)`);
      if (rendered) data[field] = String(formData.get(field) || '').trim();
    });

    // Validate each field
    for (const [fieldName, value] of Object.entries(data)) {
      const fieldValidation = this.fieldValidator.validateField(fieldName, value);
      if (!fieldValidation.valid) {
        errors.push(fieldValidation.error);
        valid = false;
      }
    }

    return {
      valid,
      errors,
      message: valid ? '' : 'Please correct the errors below',
      data
    };
  }

  /**
   * Validate single field
   */
  validateField(fieldName, value) {
    return this.fieldValidator.validateField(fieldName, value);
  }

  /**
   * Get validation rules for a field
   */
  getFieldRules(fieldName) {
    return this.rulesManager.getFieldRules(fieldName);
  }

  /**
   * Update field validation rules
   */
  updateFieldRules(fieldName, newRules) {
    this.rulesManager.updateFieldRules(fieldName, newRules);
  }

  /**
   * Get all validation rules
   */
  getAllRules() {
    return this.rulesManager.getAllRules();
  }
}

export { ContactFormValidator, ValidationRulesManager, ContactFieldValidator };